import * as React from 'react';
import { HashRouter, Redirect, Route, RouteComponentProps } from 'react-router-dom';
import * as _ from 'underscore';

import { CatalogStandaloneErrorToasts } from '@tableau/data-catalog-client-components';
import { connect } from 'react-redux';
import { registerErrorStore, unregisterErrorStore } from '../../../catalog/CatalogErrorReporterService';
import { selectCatalogingOnSiteEnabled } from '../../../catalog/selectors/CatalogSelectors';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { DataCatalogUIActionsService } from '../../../ts/services/DataCatalogUIActionsService';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { AppState } from '../../state/AppState';
import { SearchSubspaceName, SpaceInfo, SpaceItemForNonEntitySpace } from '../../state/SpaceInfo';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '../RouterHelpers/QueryParamUpdateProvider';
import { RouterError } from '../RouterHelpers/RouterError';
import { TableauReactRouterSwitch } from '../RouterHelpers/TableauReactRouterSwitch';
import { ConnectedExplorerSubspaceInitializer } from '../SpaceConfig/ExplorerSubspaceInitializer';
import { ConnectedShowFilterPanelInitially } from '../SpaceConfig/ShowFilterPanelInitially';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedSearchSpace } from './ConnectedSearchSpace';

export interface SearchSpaceRouterOwnProps extends ReactDirectiveProps {}

export interface SearchSpaceRouterStateProps {
  readonly urls: SpacesSiteUrls;
  readonly catalogingOnSiteEnabled: boolean;
}

export type SearchSpaceRouterProps = SearchSpaceRouterOwnProps & SearchSpaceRouterStateProps;

// The catalog subspaces catch and render errors via DC3 infrastructure, which we access via
// the CatalogStandaloneErrorToasts component. We must add this component at the router level,
// rather than in the subspace, because it reacts to URL updates from React Router (so it can't
// be hidden from the router by pure components).
const ErrorToastsForCatalog = (
  <CatalogStandaloneErrorToasts
    uiActions={DataCatalogUIActionsService.instance()}
    registerErrorStore={registerErrorStore}
    unregisterErrorStore={unregisterErrorStore}
  />
);

export class SearchSpaceRouter extends React.PureComponent<SearchSpaceRouterProps> {
  public render() {
    return (
      <HashRouter>
        <TableauReactRouterSwitch baseUrl={this.props.urls.searchPath()}>
          <Route exact path={this.props.urls.searchViewsPath()} render={this.componentForRoute('views')} />
          <Route exact path={this.props.urls.searchWorkbooksPath()} render={this.componentForRoute('workbooks')} />
          <Route exact path={this.props.urls.searchDatasourcesPath()} render={this.componentForRoute('datasources')} />
          <Route exact path={this.props.urls.searchFlowsPath()} render={this.componentForRoute('flows')} />
          <Route exact path={this.props.urls.searchProjectsPath()} render={this.componentForRoute('projects')} />
          { this.props.catalogingOnSiteEnabled  &&
            <Route exact path={this.props.urls.searchTablesPath()} render={this.componentForRoute('tables', ErrorToastsForCatalog)} />
          }
          { this.props.catalogingOnSiteEnabled  &&
            <Route exact path={this.props.urls.searchDatabasesPath()} render={this.componentForRoute('databases', ErrorToastsForCatalog)} />
          }
          { FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled &&
            <Route exact path={this.props.urls.searchDataRolesPath()} render={this.componentForRoute('dataRoles')} />
          }
          <Redirect exact from={this.props.urls.searchPath()} to={this.props.urls.searchViewsPath()} />
          <Route render={this.error} />
        </TableauReactRouterSwitch>
      </HashRouter>
    );
  }

  private componentForRoute = (subspaceName: SearchSubspaceName, extraChildren?: React.ReactNode) => {
    return (routeProps: RouteComponentProps): JSX.Element => {

      const routeLocationSearch = routeProps.location.search;
      const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
        routeLocationPath: routeProps.location.pathname,
        routeLocationSearch: routeLocationSearch,
        updateContentTypes: false,
        updateFilters: true,
        updateBreadcrumbs: false,
        updateSort: true
      };

      const spaceInfo: SpaceInfo = {
        item: SpaceItemForNonEntitySpace,
        spaceType: 'search',
        subspaceName: subspaceName,
      };

      return (
        <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
          <ConnectedExplorerSubspaceInitializer routeLocationSearch={routeLocationSearch} spaceInfo={spaceInfo}>
            <ConnectedShowFilterPanelInitially>
              <ConnectedSearchSpace angularContext={this.props.angularContext} subspaceName={subspaceName} />
              { extraChildren }
            </ConnectedShowFilterPanelInitially>
          </ConnectedExplorerSubspaceInitializer>
        </ConnectedQueryParamUpdateProvider>
      );
    };
  }

  private error = (_props: RouteComponentProps): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls}/>;
  }
}

const mapStateToProps: MapStateToProps<SearchSpaceRouterStateProps> = (state: AppState) => ({
  urls: siteUrlsSelector(state),
  catalogingOnSiteEnabled: selectCatalogingOnSiteEnabled(state)
});

export const ConnectedSearchSpaceRouter: React.ComponentClass<SearchSpaceRouterOwnProps>
  = connect(mapStateToProps)(SearchSpaceRouter);
